import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, useEffect, useCallback, useRef, forwardRef, } from 'react';
import { getStringLength } from '../../util/get_string_length';
import { styled } from '../../styles';
import { Subheader, AppText } from '../typography';
export const EditableNote = ({ noteText, placeholderText, description, onSave, characterLimit, disableEditing, errorMessage, hasFootBar = true, testId, showChrome = true, isEditing, showTitle = true, notesChromeStyles = {}, }) => {
    const textareaRef = useRef(null);
    const [note, setNote] = useState(noteText);
    const [noteDraft, setNoteDraft] = useState(noteText);
    const noteLimitReached = !!noteDraft && noteDraft.length >= characterLimit;
    const [edit, setEdit] = useState(false);
    const [showSavedMessage, setShowSavedMessage] = useState(false);
    const timeoutRef = useRef();
    const debounceSave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
        timeoutRef.current = setTimeout(() => {
            const updatedNote = noteDraft ? noteDraft.trim() : '';
            if (updatedNote !== note)
                setShowSavedMessage(true);
            setNote(updatedNote);
            onSave(updatedNote);
        }, 500);
    }, [note, noteDraft, onSave]);
    const resizeTextarea = useCallback(() => {
        requestAnimationFrame(() => {
            if (textareaRef.current) {
                textareaRef.current.style.height = '5px';
                textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 8}px`;
            }
        });
    }, []);
    const stopEdit = useCallback(() => {
        setEdit(false);
        resizeTextarea();
    }, [setEdit, resizeTextarea]);
    const handleFocusLost = useCallback(() => {
        stopEdit();
    }, [stopEdit]);
    const startEdit = useCallback(() => {
        setEdit(true);
        setNoteDraft(note);
        textareaRef.current.focus();
        // sets the cursor to the end of the text when there is text in the textarea
        const textLength = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(textLength, textLength);
        resizeTextarea();
    }, [note, resizeTextarea]);
    const handleFocus = useCallback(() => {
        if (edit) {
            return;
        }
        startEdit();
    }, [edit, startEdit]);
    const handleKeyUp = useCallback((e) => {
        if (e.key === 'Escape') {
            handleFocusLost();
            setNoteDraft(note);
            textareaRef.current.blur();
        }
    }, [handleFocusLost, note]);
    const handleBlur = () => {
        debounceSave();
        handleFocusLost();
    };
    const renderCharCount = noteDraft ? `${getStringLength(noteDraft)}/${characterLimit}` : '';
    const handleInputChange = useCallback((e) => {
        let draft = e.target.value;
        if (noteLimitReached) {
            draft = draft.substring(0, characterLimit);
        }
        setNoteDraft(draft);
    }, [characterLimit, noteLimitReached]);
    useEffect(() => {
        resizeTextarea();
    }, [note, noteDraft, resizeTextarea]);
    useEffect(() => {
        setNote(noteText);
        setNoteDraft(noteText);
    }, [noteText]);
    useEffect(() => {
        if (!edit && textareaRef.current.scrollTop !== 0) {
            textareaRef.current.scrollTop = 0;
        }
    }, [edit, noteDraft]);
    useEffect(() => {
        if (isEditing) {
            startEdit();
        }
    }, [isEditing, startEdit]);
    return (_jsxs(NoteContainer, { "data-testid": testId, children: [_jsx(EditableNotesHeader, { description: description, errorMessage: errorMessage, edit: edit, renderCharCount: renderCharCount, showSavedMessage: showSavedMessage, noteLimitReached: noteLimitReached, showTitle: showTitle, notesChromeStyles: notesChromeStyles, showChrome: showChrome }), _jsx(NotesTextArea, { ref: textareaRef, placeholderText: disableEditing ? 'No call notes' : placeholderText, characterLimit: characterLimit, initialNote: note, currentNote: noteDraft, isEditingYn: edit, isEditingDisabledYn: disableEditing, handleKeyUp: handleKeyUp, handleInputChange: handleInputChange, handleFocus: handleFocus, handleBlur: handleBlur, errorMessage: errorMessage, isBrowserChromeYn: showChrome, showBottomBorderYn: hasFootBar })] }));
};
EditableNote.displayName = 'EditableNote';
const EditableNotesHeader = ({ showTitle, description, errorMessage, showSavedMessage, edit, renderCharCount, showChrome = true, notesChromeStyles, noteLimitReached, }) => {
    return (_jsxs(NotesChrome, { showChrome: showChrome, notesChromeStyles: notesChromeStyles, children: [showTitle && (_jsx(Subheader, { id: "call-notes-heading", "data-testid": "call-notes-heading", size: "mediumLarge", children: description })), _jsxs(MessageContainer, { description: description, children: [!!errorMessage && (_jsx(AppText, { "data-testid": "notes-error-message", color: "red50", children: errorMessage })), showSavedMessage && !edit && !errorMessage && (_jsx(AppText, { color: "medium", children: "Saved!" })), (showChrome || edit || !!errorMessage) && (_jsx(CharCounter, { atCharLimit: noteLimitReached, children: renderCharCount }))] })] }));
};
export const NotesTextArea = forwardRef(({ placeholderText, characterLimit, handleKeyUp, isEditingYn, isEditingDisabledYn, initialNote, currentNote, handleInputChange, handleFocus, handleBlur, errorMessage, isBrowserChromeYn, showBottomBorderYn, }, textareaRef) => {
    const value = isEditingYn ? currentNote : initialNote;
    return (_jsx(NoteTextArea, { "aria-labelledby": "call-notes-heading", "data-testid": "call-note-textarea-input", "data-clarity-mask": "true", ref: textareaRef, onKeyUp: handleKeyUp, value: value, onChange: handleInputChange, onFocus: handleFocus, onBlur: handleBlur, isEditing: isEditingYn, placeholder: isEditingDisabledYn ? 'No call notes' : placeholderText, maxLength: characterLimit, disabled: isEditingDisabledYn, error: !!errorMessage, hasFootBar: showBottomBorderYn, showChrome: isBrowserChromeYn, className: isEditingYn ? 'focus-visible' : '' }));
});
NotesTextArea.displayName = 'NotesTextArea';
const CharCounter = styled.span(({ theme, atCharLimit }) => ({
    fontSize: theme.text.size.medium,
    fontFamily: theme.text.bodyFontFamily,
    fontWeight: 400,
    flexGrow: 1,
    textAlign: 'right',
    color: atCharLimit ? theme.colors.red50 : theme.colors.grey60,
    paddingLeft: theme.spacing.s,
}));
const NoteContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
}));
const NoteTextArea = styled.textarea(({ theme, isEditing, error, hasFootBar, showChrome, disabled }) => ({
    minHeight: '4.5rem',
    maxHeight: '15.875rem',
    padding: showChrome
        ? '4px 6px !important'
        : isEditing || error
            ? `${theme.spacing.xxs} ${theme.spacing.xs}`
            : `${theme.spacing.xxs} 0`,
    marginTop: theme.spacing.xxs,
    flexGrow: 1,
    fontSize: theme.text.size.medium,
    fontFamily: theme.text.bodyFontFamily,
    backgroundColor: isEditing ? theme.colors.white : theme.colors.transparent,
    outline: 'none',
    borderRadius: theme.radii[1],
    resize: 'none',
    overflowY: 'auto',
    border: isEditing
        ? `1px solid ${theme.colors.transparent}`
        : error
            ? `1px solid ${theme.colors.red50}`
            : showChrome
                ? `1px solid ${theme.colors.grey10}`
                : `1px solid ${theme.colors.transparent}`,
    borderBottom: hasFootBar && !isEditing && !error && !showChrome
        ? `1px solid ${theme.colors.grey10}`
        : null,
    '.focus-visible.focus-visible': isEditing ?? {
        boxShadow: 'none',
    },
    ':disabled': {
        color: '#000000',
    },
    ':hover': {
        backgroundColor: !isEditing && !disabled ? theme.colors.lightBlue : theme.colors.white,
        cursor: disabled ? 'not-allowed' : 'auto',
    },
    ':focus': {
        boxShadow: '2px blue',
    },
}));
const NotesChrome = styled.div(({ showChrome, theme, notesChromeStyles = {} }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '1.5rem',
    marginBottom: showChrome ? theme.spacing.xs : 0,
    ...notesChromeStyles,
}));
const MessageContainer = styled.div(({ description }) => ({
    display: 'flex',
    justifyContent: description ? 'flex-end' : 'flex-start',
    alignItems: 'center',
}));
//# sourceMappingURL=editable_note.js.map